import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type CycleCountTaskRecord = {
  id: string;
  taskNumber: string;
  facilityId: string;
  status: string;
  countType: string;
  assignedTo: string | null;
  assignedToUserName: string | null;
  isBlindCount: boolean;
  isFrozen: boolean;
  dueDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CycleCountTaskSortOption =
  | 'TaskNumberAsc'
  | 'TaskNumberDesc'
  | 'StatusAsc'
  | 'StatusDesc'
  | 'CountTypeAsc'
  | 'CountTypeDesc'
  | 'DueDateAsc'
  | 'DueDateDesc'
  | 'CreatedAtAsc'
  | 'CreatedAtDesc';

export const cycleCountTasksQueryAtom = createQueryAtomFactory(
  'inventory.cycleCountTasks',
  (params: {
    status?: string;
    countType?: string;
    facilityIds?: string[];
    limit?: number;
    offset?: number;
    sorting?: CycleCountTaskSortOption[];
  }) => ({
    cycleCountTasks: {
      __args: {
        filter: {
          status: params.status,
          facilityIds: params.facilityIds,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        taskNumber: true,
        facilityId: true,
        status: true,
        countType: true,
        assignedTo: true,
        assignedToUser: {
          id: true,
          firstName: true,
          lastName: true,
        },
        isBlindCount: true,
        isFrozen: true,
        dueDate: true,
        notes: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      tasks: CycleCountTaskRecord[];
      totalCount: number;
    } => {
      if (!data || !data.cycleCountTasks || !data.cycleCountTasks.entries) {
        return { tasks: [], totalCount: 0 };
      }

      const tasks = data.cycleCountTasks.entries.map((task) => ({
        id: task.id,
        taskNumber: task.taskNumber,
        facilityId: task.facilityId,
        status: task.status,
        countType: task.countType,
        assignedTo: task.assignedTo || null,
        assignedToUserName: task.assignedToUser
          ? `${task.assignedToUser.firstName ?? ''} ${task.assignedToUser.lastName ?? ''}`.trim()
          : null,
        isBlindCount: task.isBlindCount,
        isFrozen: task.isFrozen,
        dueDate: task.dueDate || null,
        notes: task.notes || null,
        createdAt: task.timestamps?.createdAt || '',
        updatedAt: task.timestamps?.updatedAt || '',
      }));

      return {
        tasks,
        totalCount: data.cycleCountTasks.page?.total || 0,
      };
    },
  },
);
